// EmployerFormDetails.jsx

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { styled, useTheme } from "@mui/system";
import useMediaQuery from "@mui/material/useMediaQuery";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

// Service + Constants
import employerSvc from "../../services/employer/employerSvc";
import { makeTracker } from "../../services/track/trackSvc";
import { PLAN_TYPE_MAP, IS_DEV } from "../../utils/constants";
import { isTrustedParentMessage } from "../../utils/iframeMessaging";

// Child Components
import PlanDescription from "./components/PlanDescription";
import Forms from "./components/Forms";
import FAQs from "./components/FAQs";
import Resources from "./components/Resources";

const ContainerBox = styled(Container)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  [theme.breakpoints.up("sm")]: {
    paddingTop: 35,
    paddingBottom: 35,
  },
}));

const CardContentBox = styled(CardContent)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
});

const CustomTabs = styled((props) => <Tabs {...props} />)(({ theme }) => ({
  borderBottom: "none",
  backgroundColor: "#F9F9F9",
  "& .MuiTabs-flexContainer": {
    justifyContent: "flex-start !important",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "white",
  },
}));

const CustomTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  minWidth: 40,
  fontSize: "16px",
  fontWeight: "regular",
  marginRight: "8px",
  color: "#40a9ff",
  backgroundColor: "#F9F9F9",
  fontFamily: [
    "-apple-system",
    "BlinkMacSystemFont",
    '"Segoe UI"',
    "Roboto",
    '"Helvetica Neue"',
    "Arial",
    "sans-serif",
  ].join(","),
  "&:hover": {
    color: "#40a9ff",
    opacity: 1,
  },
  "&.Mui-selected": {
    color: "#1F1F1F",
    backgroundColor: "white",
    borderTop: "1px solid #D8D8D8",
    borderLeft: "1px solid #D8D8D8",
    borderRight: "1px solid #D8D8D8",
    fontWeight: "medium",
  },
}));

function EmployerFormDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Debug flag - set to true to enable console logs
  const DEBUG_ROUTING = false;

  const debugLog = useCallback((message, data = null) => {
    if (DEBUG_ROUTING) {
      if (data) {
        console.log('EmployerFormDetails:', message, data);
      } else {
        console.log('EmployerFormDetails:', message);
      }
    }
  }, [DEBUG_ROUTING]);

  // Log when component mounts
  useEffect(() => {
    debugLog("EmployerFormDetails component mounted");
  }, [debugLog]);

  // New: State to store parent website
  const [parentWebsite, setParentWebsite] = useState(null);

  // Listen for postMessage from parent for website info
  useEffect(() => {
    function handleMessage(event) {
      debugLog("EmployerFormDetails received message:", event.data);
      if (!isTrustedParentMessage(event)) {
        debugLog("Ignoring message from untrusted source:", event.origin);
        return;
      }
      if (event.data && event.data.website) {
        setParentWebsite(event.data.website);
        debugLog("Received parent website via postMessage:", event.data.website);
      } else if (event.data && event.data.type === 'ROUTE_CHANGE' && event.data.website) {
        setParentWebsite(event.data.website);
        debugLog("Received parent website via ROUTE_CHANGE:", event.data.website);
      } else if (event.data && event.data.type === 'WEBSITE_INFO' && event.data.website) {
        setParentWebsite(event.data.website);
        debugLog("Received parent website via WEBSITE_INFO:", event.data.website);
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [debugLog]);

  // Compute websiteName, prefer parentWebsite if present
  const websiteName = useMemo(() => {
    if (parentWebsite) {
      switch (parentWebsite) {
        case "region10rams.org":
          return "Region 10 RAMS";
        case "calstrs403bcomply.com":
          return "CALSTRS";
        case "tcgservices.com":
          return "TCG Services";
        // Add more mappings as needed
        default:
          return parentWebsite;
      }
    }
    // Default to TCG Services if no parentWebsite provided
    return "TCG Services";
  }, [parentWebsite]);

  // Parse segments from URL path
  const employerId = location.pathname.split("/")[2];

  // State
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [planGroups, setPlanGroups] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  const [approvedVendors, setApprovedVendors] = useState([]);
  const [employerInfo, setEmployerInfo] = useState({
    employerName: null,
    state: null,
  });
  const [payrollInfo, setPayrollInfo] = useState([]);
  const [empLogo, setEmpLogo] = useState([]);
  const [selectedPlanTab, setSelectedPlanTab] = useState("");
  const [selectedPlan, setSelectedPlan] = useState({});
  const [selectedPlanType, setSelectedPlanType] = useState({
    label: "",
    code: "",
    description: "",
  });

  // We'll store reliusWeb separately (or you can store it inside employerInfo)
  const [reliusWeb, setReliusWeb] = useState("retirement.tcgservices.com");

  // Usage beacon, bound to this page's employer/website context once so the
  // child components only pass the item they represent. websiteName falls back
  // to "TCG Services" while parentWebsite is still arriving over postMessage,
  // so early clicks are attributed rather than dropped.
  const track = useMemo(
    () =>
      makeTracker({
        employerIdForURL: employerId,
        employer_name: employerInfo?.employerName,
        website: websiteName,
        page: "formSearch",
      }),
    [employerId, employerInfo?.employerName, websiteName]
  );

  // Handle tab change. Switching plan tabs is the truest "viewed this plan"
  // signal - it happens without any navigation, so nothing else records it.
  const handleSelectedPlanTabChange = (_, newValue) => {
    setSelectedPlanTab(newValue);
    track({
      event_type: "view",
      item_type: "plan",
      item_id: newValue,
      item_name: PLAN_TYPE_MAP?.[newValue]?.label || newValue,
      plan_type: newValue,
    });
  };

  // Enrollment handler (non-admin)
  const handleEnroll = () => {
    const planPassword = selectedPlan?.planData?.find(
      (d) => d.field === "planPassword"
    )?.value;
    // rely on the stored reliusWeb, fallback if needed
    const finalReliusWeb = reliusWeb || "retirement.tcgservices.com";
    
    debugLog("handleEnroll called with:", {
      planPassword,
      finalReliusWeb,
      selectedPlanType: selectedPlanType.label
    });
    
    const formData = {
      LoginButton: "true",
      RANDOMNUM: "",
      PLANID: "",
      PIN: planPassword,
      DROP: "",
    };
    
    // Use external navigation with form data
    debugLog("Checking for window.triggerReliusNavigation:", !!window.triggerReliusNavigation);
    if (window.triggerReliusNavigation) {
      debugLog("Using window.triggerReliusNavigation");
      window.triggerReliusNavigation(finalReliusWeb, formData);
    } else {
      debugLog("Using fallback form submission to:", `https://${finalReliusWeb}/enrollinplan.aspx`);
      // Fallback to direct form submission if function not available
      const url = `https://${finalReliusWeb}/enrollinplan.aspx`;
      post(url, formData);
    }
  };

  // Helper function to post a hidden form
  function post(path, params, method = "post") {
    debugLog("Post function called with:", { path, params, method });
    const form = document.createElement("form");
    form.method = method;
    form.action = path;
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        const hiddenField = document.createElement("input");
        hiddenField.type = "hidden";
        hiddenField.name = key;
        hiddenField.value = params[key];
        form.appendChild(hiddenField);
      }
    }
    document.body.appendChild(form);
    debugLog("Form created and about to submit to:", path);
    form.submit();
    debugLog("Form submitted");
  }

  // Combined effect to fetch both reliusWeb (from getEmployersByWebsite) and other details
  useEffect(() => {
    if (!employerId) return;
    
    // Make API call if we have a parentWebsite OR if we're in standalone mode (localhost)
    // This ensures we make the call for all parent websites, including tcgservices.com
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (!parentWebsite && !isLocalhost) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        debugLog("Making API calls with websiteName:", websiteName);

        // 1) fetch the entire list of employers for this website
        const allEmployers = await employerSvc.getEmployersByWebsite({
          website: websiteName,
          page: "formSearch",
        });

        // 2) find the employer in that list that matches employerId
        const matchedEmployer = allEmployers.find(
          (e) => String(e.employerIdForURL) === String(employerId)
        );

        // 3) store relius_web or fallback
        const foundRelius =
          matchedEmployer?.relius_web || "retirement.tcgservices.com";
        setReliusWeb(foundRelius);

        // 4) now fetch the other employer details
        const {
          employerInfo,
          documents,
          plans,
          planGroups,
          approvedVendors,
          payroll_dates,
          logo,
        } = await employerSvc.getEmployerDetails({
          employerIdForURL: employerId,
          page: "formSearch",
          // pass websiteName if your API needs it
          website: websiteName,
        });

        // 5) store everything else
        setEmployerInfo(employerInfo || { employerName: null, state: null });
        setDocuments(documents || []);
        setPlans(plans || []);
        setPlanGroups(planGroups || []);
        setApprovedVendors(approvedVendors || []);
        setPayrollInfo(payroll_dates || []);
        setEmpLogo(logo || []);

        debugLog("API calls completed successfully");
      } catch (error) {
        console.error("Error fetching employer data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [employerId, websiteName, parentWebsite, debugLog]);

  // Build planTypes with friendly labels
  useEffect(() => {
    const types = plans.map((item) => {
      const code = item.planUniqueId.split("_")[1];
      const label = PLAN_TYPE_MAP[code]?.label || code;
      
      // Debug logging for plan types
      debugLog('Plan type mapping:', {
        planUniqueId: item.planUniqueId,
        extractedCode: code,
        label: label,
        fullItem: item
      });
      
      return { code, label };
    });
    setPlanTypes(types);

    // default to the first plan tab if any
    if (types.length > 0) {
      setSelectedPlanTab(types[0].code);
    }
  }, [plans, debugLog]);

  // Update selected plan data on tab change
  useEffect(() => {
    if (selectedPlanTab) {
      const currentPlan = plans.find(
        (p) => p.planUniqueId.split("_")[1] === selectedPlanTab
      );
      if (currentPlan) {
        const derivedCode = currentPlan.planUniqueId.split("_")[1];
        setSelectedPlan(currentPlan);

        // Example snippet in EmployerFormDetails.jsx:
        if (payrollInfo.length) {
          // console.log(payrollInfo)
          // console.log(selectedPlanTab)
          // console.log(selectedPlanType.label)
          setSelectedPlanType({
            label: currentPlan.label,
            code: derivedCode,
            description: currentPlan.planDesc,
            showSPD: currentPlan.showSPD ?? true,
            showPayrollDates: currentPlan.showPayrollDates ?? false,
            searchEnroll: currentPlan.searchEnroll,
            // Here is where we store the friendly label from the API:
            payrollTitle:
              payrollInfo[0].plans.filter(
                (item) => item.PayrollDatesPlanDesc === selectedPlanType.code
              ).length > 0
                ? payrollInfo[0].plans.filter(
                    (item) => item.PayrollDatesPlanDesc === selectedPlanType.code
                  )[0].PayrollDatesDisplayName
                : "Contribution Deadline Dates",  // fallback if missing
          });
        } else {
          const newSelectedPlanType = {
            label: currentPlan.label,
            code: derivedCode,
            description: currentPlan.planDesc,
            showSPD: currentPlan.showSPD ?? true,
            showPayrollDates: currentPlan.showPayrollDates ?? false,
            searchEnroll: currentPlan.searchEnroll,
            payrollTitle: "Contribution Deadline Dates",
          };

          // Debug logging for selectedPlanType
          debugLog('Setting selectedPlanType:', {
            derivedCode,
            newSelectedPlanType,
            currentPlan: currentPlan
          });

          setSelectedPlanType(newSelectedPlanType);
        }
      }
    }
  }, [plans, selectedPlanTab, payrollInfo, selectedPlanType.code, debugLog]);

  // planPassword checks
  const planPassword = selectedPlan?.planData?.find(
    (d) => d.field === "planPassword"
  )?.value;
  const showEnrollButton =
    selectedPlanType.code !== "FICA" &&
    Boolean(planPassword) &&
    selectedPlanType.searchEnroll !== false;

  const distributionUrl = IS_DEV
    ? "https://distributions-dev.tcgservices.com/"
    : "https://distributions.tcgservices.com";
  const showDistributionButton = selectedPlan?.searchDistribution === true;

  // "Admin" plans check
  const isAdminPlan =
    selectedPlanType.code === "403bAdmin" ||
    selectedPlanType.code === "457bAdmin";
    
  debugLog("Plan state:", {
    selectedPlanTab,
    selectedPlanType,
    isAdminPlan,
    showEnrollButton,
    planPassword: selectedPlan?.planData?.find((d) => d.field === "planPassword")?.value
  });

  return (
    <React.Fragment>
      <Container component="main">
        {loading ? (
          <Container
            fixed
            maxWidth="md"
            sx={{
              height: 500,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress size={68} />
          </Container>
        ) : (
          <ContainerBox maxWidth="md">
            <Card>
              <CardContentBox>
                {/* Employer Logo or Name */}
                {empLogo === "N/A" ? (
                  <Typography variant="h5" gutterBottom>
                    {employerInfo.employerName}
                  </Typography>
                ) : (
                  <img
                    src={empLogo}
                    alt="Employer Logo"
                    style={{
                      maxWidth: 405,
                      maxHeight: 124,
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain'
                    }}
                  />
                )}

                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{ textAlign: "center", my: 2 }}
                >
                  Please select your plan from the tabs below for more
                  information and to gain access to your plan forms.
                </Typography>

                {/* Plan Tabs */}
                <CustomTabs
                  value={selectedPlanTab}
                  onChange={handleSelectedPlanTabChange}
                  variant={isMobile ? "scrollable" : "standard"}
                  scrollButtons={isMobile ? "auto" : false}
                  allowScrollButtonsMobile
                  indicatorColor="primary"
                  textColor="primary"
                  sx={{
                    "& .MuiTabs-flexContainer": {
                      justifyContent: "flex-start !important",
                    },
                  }}
                >
                  {planTypes.map((item) => (
                    <CustomTab key={item.code} label={item.label} value={item.code} />
                  ))}
                </CustomTabs>

                <Grid
                  container
                  spacing={2}
                  sx={{ width: "95%", mx: "auto", mt: 2 }}
                >
                  <Grid item xs={12}>
                    {/* Enroll / Login / Distribution Buttons */}
                    {(showEnrollButton || showDistributionButton) && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 2,
                          mt: 1,
                        }}
                      >
                        {showEnrollButton && (
                          <>
                            {isAdminPlan ? (
                              <Button
                                fullWidth={isMobile}
                                disableElevation
                                variant="contained"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  debugLog("Admin plan button clicked");
                                  // Check if there are vendors for this admin plan
                                  const hasVendors = approvedVendors && approvedVendors.some(vendor => {
                                    // Normalize both plan types for comparison
                                    const normalizedVendorPlanType = vendor.planType.replace(/\s/g, "").replace(/[()]/g, "").toLowerCase();
                                    const normalizedPlanType = selectedPlanType.label.replace(/\s/g, "").replace(/[()]/g, "").toLowerCase();
                                    return normalizedVendorPlanType === normalizedPlanType;
                                  });

                                  debugLog("Vendor check result:", { hasVendors, approvedVendorsCount: approvedVendors?.length });

                                  if (hasVendors) {
                                    // Navigate to approved vendors page
                                    const vendorUrl = `/search-enroll/${employerId}/${employerInfo.state}/approved-vendor/${encodeURIComponent(
                                      selectedPlanType.label
                                    )}`;
                                    debugLog("Navigating to vendors page:", vendorUrl);
                                    navigate(vendorUrl);
                                  } else {
                                    // No vendors for this admin plan, use enrollment logic
                                    debugLog("No vendors found, calling handleEnroll");
                                    handleEnroll();
                                  }
                                }}
                                sx={{
                                  fontSize: isMobile ? "0.8rem" : "1rem",
                                  padding: isMobile ? "6px 10px" : "8px 16px",
                                }}
                              >
                                Enroll (New User)
                              </Button>
                            ) : (
                              <Button
                                fullWidth={isMobile}
                                disableElevation
                                variant="contained"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  debugLog("Non-admin plan button clicked");
                                  handleEnroll();
                                }}
                                sx={{
                                  fontSize: isMobile ? "0.8rem" : "1rem",
                                  padding: isMobile ? "6px 10px" : "8px 16px",
                                }}
                              >
                                Enroll (New User)
                              </Button>
                            )}
                            {/* Existing users login */}
                            <Button
                              fullWidth={isMobile}
                              disableElevation
                              variant="outlined"
                              href={`https://${reliusWeb}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                fontSize: isMobile ? "0.8rem" : "1rem",
                                padding: isMobile ? "6px 10px" : "8px 16px",
                              }}
                            >
                              Login (Existing User)
                            </Button>
                          </>
                        )}
                        {showDistributionButton && (
                          <Button
                            fullWidth={isMobile}
                            disableElevation
                            variant="outlined"
                            href={distributionUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              fontSize: isMobile ? "0.8rem" : "1rem",
                              padding: isMobile ? "6px 10px" : "8px 16px",
                            }}
                          >
                            Request a Distribution
                          </Button>
                        )}
                      </Box>
                    )}

                    {/* Plan Description */}
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      {selectedPlanType.description}
                    </Typography>

                    {/* Plan details, if any */}
                    {(plans.length > 0 || documents.length > 0) && (
                      <>
                        {/* Debug info */}
                        {debugLog('Debug info:', {
                          documentsCount: documents.length,
                          selectedPlanType,
                          allDocuments: documents
                        })}
                      <List sx={{ mt: 1 }}>
                        <PlanDescription
                          selectedPlanTab={selectedPlanTab}
                          selectedPlanType={selectedPlanType}
                          selectedPlanUniversalPlanValue={
                            selectedPlan.universalPlanValue
                          }
                          employerState={employerInfo.state}
                          parentWebsite={parentWebsite}
                          planDescriptionDocuments={documents.filter(
                            (doc) => {
                              // Debug: log all available fields in the document
                              debugLog('Full document:', doc);

                              // Check if the document matches the current plan type
                              const docPlanType = doc.planType || doc.docType?.split(" - ")[0]?.replace(" ", "");
                              const matchesPlanType = docPlanType === selectedPlanType.code;

                              // Check if the document belongs to "Plan Description" section
                              // Try multiple possible field names for display section
                              const displaySection = doc.displaySection || doc.docType?.split(" - ")[1] || doc.section;
                              const isPlanDescription = displaySection === "Plan Description";

                              // Debug logging
                              debugLog('Document filtering:', {
                                docName: doc.docName || doc.documentName,
                                docPlanType,
                                selectedPlanTypeCode: selectedPlanType.code,
                                matchesPlanType,
                                displaySection,
                                isPlanDescription,
                                willInclude: matchesPlanType && isPlanDescription
                              });

                              return matchesPlanType && isPlanDescription;
                            }
                          )}
                          approvedVendors={approvedVendors}
                          showPayroll={selectedPlanType.showPayrollDates}
                          payrollTitle={selectedPlanType.payrollTitle}
                          track={track}
                        />
                        <Forms
                          formDocuments={documents.filter(
                            (doc) => {
                              // Use the same logic as Plan Description filtering
                              const docPlanType = doc.planType || doc.docType?.split(" - ")[0]?.replace(" ", "");
                              const matchesPlanType = docPlanType === selectedPlanType.code;
                              const displaySection = doc.displaySection || doc.docType?.split(" - ")[1] || doc.section;
                              const isForms = displaySection === "Forms";
                              
                              // Debug logging for Forms filtering
                              if (doc.docName && doc.docName.includes("FICA Alternative")) {
                                debugLog('Forms filtering debug:', {
                                  docName: doc.docName,
                                  docPlanType,
                                  docPlanTypeRaw: doc.planType,
                                  docTypeRaw: doc.docType,
                                  selectedPlanTypeCode: selectedPlanType.code,
                                  matchesPlanType,
                                  displaySection,
                                  isForms,
                                  willInclude: matchesPlanType && isForms,
                                  fullDoc: doc
                                });
                              }
                              
                              return matchesPlanType && isForms;
                            }
                          )}
                          track={track}
                        />
                        <Resources
                          resourceDocuments={documents.filter(
                            (doc) => {
                              // Use the same logic as Plan Description filtering
                              const docPlanType = doc.planType || doc.docType?.split(" - ")[0]?.replace(" ", "");
                              const matchesPlanType = docPlanType === selectedPlanType.code;
                              const displaySection = doc.displaySection || doc.docType?.split(" - ")[1] || doc.section;
                              const isResources = displaySection === "Resources";
                              
                              return matchesPlanType && isResources;
                            }
                          )}
                          track={track}
                        />
                        <FAQs
                          planGroups={planGroups}
                          selectedPlanType={selectedPlanType}
                          track={track}
                        />
                      </List>
                      </>
                    )}
                  </Grid>
                </Grid>
              </CardContentBox>
            </Card>
          </ContainerBox>
        )}
      </Container>
    </React.Fragment>
  );
}

export default EmployerFormDetails;
